import React, { useState, useCallback, useEffect, useRef } from 'react';
import ReactFlow, {
  MiniMap,
  Controls,
  Background,
  useNodesState,
  useEdgesState,
  addEdge,
  MarkerType,
  useReactFlow,
  ReactFlowProvider,
} from 'reactflow';
import 'reactflow/dist/style.css';
import { useTheme } from '../contexts/ThemeContext';
import { MessageSquare, Menu, Clock, CheckCircle, Plus, Save, X, Trash2, Search, Tag } from 'lucide-react';
import EmojiPicker from 'emoji-picker-react';
import { AlertModal, ConfirmModal } from './FluxoEditor/Modals';
import { nodeTypes } from './FluxoEditor/NodeTypes';
import MessageVariables from './MessageVariables';
import PainelVariaveis from './FluxoEditor/PainelVariaveis';
import { templates } from './FluxoEditor/templates';
import { tagsAPI, mediaAPI } from '../services/api';
import { toast } from 'react-toastify';
import { validarTamanhoMidia } from '../utils/limitesMidiaWhatsapp';
import { useAuth } from '../contexts/AuthContext';
import TagSelector from './TagSelector';
import { resolverPreview } from '../utils/previewVariaveis';





const FluxoVisualEditorInner = ({ fluxo, onSave, onCancel, clienteId }) => {
  const { darkMode } = useTheme();
  const { user } = useAuth();
  const reactFlowInstance = useReactFlow();
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [selectedNode, setSelectedNode] = useState(null);
  const [showTemplates, setShowTemplates] = useState(!fluxo);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [formData, setFormData] = useState({
    nome: fluxo?.nome || '',
    descricao: fluxo?.descricao || '',
    ativo: fluxo?.ativo !== false,
  });
  const [tags, setTags] = useState([]);
  const [showNewTagForm, setShowNewTagForm] = useState(false);
  const [newTag, setNewTag] = useState({ nome: '', cor: '#3B82F6' });
  const [alertModal, setAlertModal] = useState({ isOpen: false, title: '', message: '', type: 'error' });
  const [confirmModal, setConfirmModal] = useState({ isOpen: false, title: '', message: '', onConfirm: null });
  const [connectMenu, setConnectMenu] = useState({ isOpen: false, x: 0, y: 0, sourceNode: null });
  const [searchTerm, setSearchTerm] = useState('');
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [previewMessages, setPreviewMessages] = useState([]);
  const [previewCurrentNode, setPreviewCurrentNode] = useState(null);
  const [previewInput, setPreviewInput] = useState('');
  const previewEndRef = useRef(null);
  const textareaRef = useRef(null);
  const emojiPickerRef = useRef(null);
  const history = useRef([]);
  const historyIndex = useRef(-1);
  const [variaveisFluxo, setVariaveisFluxo] = useState(fluxo?.variaveis || []);
  const [showVariaveis, setShowVariaveis] = useState(false);

  useEffect(() => {
    setVariaveisFluxo(fluxo?.variaveis || []);
  }, [fluxo]);

  /** Insere no cursor do textarea da mensagem, como o MessageVariables faz. */
  const inserirNoCursor = (texto) => {
    const ta = textareaRef.current;
    const atual = selectedNode?.data?.mensagem || '';
    // selectionStart de um textarea que nunca recebeu foco e 0, o que fazia o
    // chip inserir no INICIO da mensagem. Sem foco, o certo e anexar no fim.
    const focado = ta && document.activeElement === ta;
    const ini = focado ? ta.selectionStart : atual.length;
    const fim = focado ? ta.selectionEnd : atual.length;
    const novo = atual.slice(0, ini) + texto + atual.slice(fim);
    setNodes((nds) =>
      nds.map((node) =>
        node.id === selectedNode.id
          ? { ...node, data: { ...node.data, mensagem: novo } }
          : node
      )
    );
    setTimeout(() => {
      if (!ta) return;
      ta.focus();
      ta.setSelectionRange(ini + texto.length, ini + texto.length);
    }, 0);
  };


  useEffect(() => {
    if (fluxo?.etapas && fluxo.etapas.length > 0) {
      // Resolver URLs S3 das etapas antes de converter em nodes
      const resolverMediaUrls = async () => {
        for (const etapa of fluxo.etapas) {
          if (etapa.media_url?.startsWith('s3://')) {
            try {
              const res = await mediaAPI.getSignedUrl(etapa.media_url);
              if (res.data?.url) etapa.media_url = res.data.url;
            } catch (e) { /* manter URL original */ }
          }
        }
        convertFluxoToNodes(fluxo);
        setShowTemplates(false);
      };
      resolverMediaUrls();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fluxo]);

  // Carregar tags
  useEffect(() => {
    if (clienteId) {
      loadTags();
    }
  }, [clienteId]);

  const loadTags = async () => {
    try {
      const response = await tagsAPI.list(clienteId);
      setTags(response.data || []);
    } catch (error) {
      console.error('Erro ao carregar tags:', error);
    }
  };

  const handleCreateTag = async () => {
    if (!newTag.nome.trim()) return;
    try {
      const response = await tagsAPI.create({
        cliente_id: clienteId,
        nome: newTag.nome,
        cor: newTag.cor,
      });
      setTags([...tags, response.data]);
      setFormData({ ...formData, tag_atendimento_id: response.data.id });
      setNewTag({ nome: '', cor: '#3B82F6' });
      setShowNewTagForm(false);
    } catch (error) {
      console.error('Erro ao criar tag:', error);
    }
  };

  useEffect(() => {
    if (selectedNode) {
      const updatedNode = nodes.find(n => n.id === selectedNode.id);
      if (!updatedNode) {
        setSelectedNode(null);
      } else if (JSON.stringify(updatedNode.data) !== JSON.stringify(selectedNode.data)) {
        setSelectedNode(updatedNode);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nodes]);

  useEffect(() => {
    if (nodes.length > 0 || edges.length > 0) {
        setHasUnsavedChanges(true);
        // variaveisFluxo entra no rascunho: sem ela, restaurar traria os
        // placeholders e perderia a declaracao de que eles dependem.
        localStorage.setItem('fluxo_draft', JSON.stringify({ nodes, edges, formData, variaveis: variaveisFluxo }));
      
      // Adicionar ao histórico
      const newState = { nodes, edges };
      const lastState = history.current[historyIndex.current];
      if (!lastState || JSON.stringify(lastState) !== JSON.stringify(newState)) {
        const newHistory = history.current.slice(0, historyIndex.current + 1);
        newHistory.push(newState);
        if (newHistory.length > 50) newHistory.shift();
        history.current = newHistory;
        historyIndex.current = newHistory.length - 1;
      }
    }
  }, [nodes, edges, formData, variaveisFluxo]);

  useEffect(() => {
    if (previewEndRef.current) {
      previewEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [previewMessages]);

  useEffect(() => {
    if (!fluxo) {
      const draft = localStorage.getItem('fluxo_draft');
      if (draft) {
        const { nodes: draftNodes, edges: draftEdges, formData: draftForm, variaveis: draftVars } = JSON.parse(draft);
        if (draftNodes?.length > 0) {
          setNodes(draftNodes);
          setEdges(draftEdges);
          setFormData(draftForm);
          setVariaveisFluxo(draftVars || []);
          setShowTemplates(false);
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (showEmojiPicker && emojiPickerRef.current && !emojiPickerRef.current.contains(e.target)) {
        setShowEmojiPicker(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showEmojiPicker]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Delete' && selectedNode) {
        setConfirmModal({
          isOpen: true,
          title: 'Deletar Nó',
          message: `Tem certeza que deseja deletar "${selectedNode.data.label}"?\n\nEsta ação não pode ser desfeita.`,
          onConfirm: () => {
            setNodes((nds) => nds.filter(n => n.id !== selectedNode.id));
            setEdges((eds) => eds.filter(e => e.source !== selectedNode.id && e.target !== selectedNode.id));
            setSelectedNode(null);
          }
        });
      }
      if ((e.ctrlKey || e.metaKey) && e.key === 's') {
        e.preventDefault();
        handleSave();
      }
      if ((e.ctrlKey || e.metaKey) && e.key === 'z' && !e.shiftKey) {
        e.preventDefault();
        handleUndo();
      }
      if ((e.ctrlKey || e.metaKey) && (e.key === 'y' || (e.key === 'z' && e.shiftKey))) {
        e.preventDefault();
        handleRedo();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
    // variaveisFluxo entra nas deps: sem ele o Ctrl+S salvava a declaracao de
    // um render anterior e as alteracoes do painel de parametros eram perdidas.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedNode, nodes, edges, formData, variaveisFluxo]);

  // Calcular ordem dos nós (apenas quando estrutura mudar)
  const lastNodesStructure = useRef('');
  useEffect(() => {
    if (nodes.length === 0) return;
    
    // Criar hash da estrutura de conexões
    const structure = nodes.map(n => `${n.id}:${n.data.proxima_etapa_id || ''}:${n.data.opcoes?.map(o => o.proxima_etapa_id).join(',') || ''}`).join('|');
    
    // Só recalcular se estrutura mudou
    if (structure === lastNodesStructure.current) return;
    lastNodesStructure.current = structure;
    
    const ordem = new Map();
    let contador = 1;
    const visited = new Set();
    const queue = [nodes[0].id];
    
    while (queue.length > 0) {
      const nodeId = queue.shift();
      if (visited.has(nodeId)) continue;
      
      const node = nodes.find(n => n.id === nodeId);
      if (!node) continue;
      
      visited.add(nodeId);
      ordem.set(nodeId, contador++);
      
      if (node.data.opcoes) {
        node.data.opcoes.forEach(op => {
          if (op.proxima_etapa_id && !visited.has(op.proxima_etapa_id)) {
            queue.push(op.proxima_etapa_id);
          }
        });
      }
      if (node.data.proxima_etapa_id && !visited.has(node.data.proxima_etapa_id)) {
        queue.push(node.data.proxima_etapa_id);
      }
    }
    
    setNodes((nds) => nds.map(n => ({
      ...n,
      data: { ...n.data, ordem: ordem.get(n.id) }
    })));
  }, [nodes, setNodes]);

  // Sincronizar edges quando nodes mudarem (com debounce)
  const lastEdgesHash = useRef('');
  useEffect(() => {
    if (nodes.length === 0) return;
    
    const syncEdges = () => {
      const newEdges = [];
      const edgeIds = new Set();
      
      nodes.forEach((node) => {
        // Conexões de menu
        if (node.type === 'menu' && node.data.opcoes) {
          node.data.opcoes.forEach((opcao) => {
            if (opcao.proxima_etapa_id) {
              const edgeId = `e-${node.id}-${opcao.proxima_etapa_id}`;
              if (!edgeIds.has(edgeId)) {
                edgeIds.add(edgeId);
                newEdges.push({
                  id: edgeId,
                  source: node.id,
                  target: opcao.proxima_etapa_id,
                  label: `${opcao.opcao}: ${opcao.texto}`,
                  type: 'smoothstep',
                  animated: true,
                  style: { stroke: '#10b981', strokeWidth: 3 },
                  labelStyle: { fill: '#10b981', fontWeight: 700, fontSize: 12 },
                  labelBgStyle: { fill: darkMode ? '#27272a' : '#fff', fillOpacity: 0.95 },
                  labelBgPadding: [8, 4],
                  labelBgBorderRadius: 4,
                  markerEnd: { type: MarkerType.ArrowClosed, color: '#10b981', width: 20, height: 20 },
                });
              }
            }
          });
        }
        
        // Conexões manuais (proxima_etapa_id)
        if (node.data.proxima_etapa_id) {
          const edgeId = `e-${node.id}-${node.data.proxima_etapa_id}`;
          if (!edgeIds.has(edgeId)) {
            edgeIds.add(edgeId);
            newEdges.push({
              id: edgeId,
              source: node.id,
              target: node.data.proxima_etapa_id,
              type: 'smoothstep',
              animated: true,
              style: { stroke: '#3b82f6', strokeWidth: 3 },
              markerEnd: { type: MarkerType.ArrowClosed, color: '#3b82f6', width: 20, height: 20 },
            });
          }
        }
        
        // Conexões de fallback (timeout)
        if (node.data.fallback_etapa_id) {
          const edgeId = `e-fallback-${node.id}-${node.data.fallback_etapa_id}`;
          if (!edgeIds.has(edgeId)) {
            edgeIds.add(edgeId);
            newEdges.push({
              id: edgeId,
              source: node.id,
              target: node.data.fallback_etapa_id,
              label: '⏰ Timeout',
              type: 'smoothstep',
              animated: false,
              style: { stroke: '#f97316', strokeWidth: 2, strokeDasharray: '5,5' },
              labelStyle: { fill: '#f97316', fontWeight: 600, fontSize: 11 },
              labelBgStyle: { fill: darkMode ? '#27272a' : '#fff', fillOpacity: 0.95 },
              labelBgPadding: [6, 3],
              labelBgBorderRadius: 4,
              markerEnd: { type: MarkerType.ArrowClosed, color: '#f97316', width: 16, height: 16 },
            });
          }
        }
      });
      
      // Criar hash dos edges para comparação
      const edgesHash = newEdges.map(e => `${e.id}:${e.label || ''}`).sort().join('|');
      
      // Só atualizar se mudou
      if (edgesHash !== lastEdgesHash.current) {
        lastEdgesHash.current = edgesHash;
        setEdges(newEdges);
      }
    };
    
    // Debounce para evitar múltiplas atualizações
    const timer = setTimeout(syncEdges, 100);
    return () => clearTimeout(timer);
  }, [nodes, darkMode, setEdges]);

  const convertFluxoToNodes = (fluxoData) => {
    try {
      if (!fluxoData?.etapas || !Array.isArray(fluxoData.etapas)) {
        console.error('Dados de fluxo inválidos');
        return;
      }

      // Mapear conexões para calcular níveis
      const connections = new Map();
      fluxoData.etapas.forEach(etapa => {
        const targets = [];
        if (etapa.opcoes_menu) {
          etapa.opcoes_menu.forEach(op => {
            if (op.proxima_etapa_id) targets.push(op.proxima_etapa_id);
          });
        }
        if (etapa.proxima_etapa_id) targets.push(etapa.proxima_etapa_id);
        connections.set(etapa.id, targets);
      });

      // Calcular níveis hierárquicos
      const levels = new Map();
      const calculateLevel = (id, level = 0, visited = new Set()) => {
        if (visited.has(id)) return;
        visited.add(id);
        
        if (!levels.has(id) || levels.get(id) < level) {
          levels.set(id, level);
          const targets = connections.get(id) || [];
          targets.forEach(targetId => calculateLevel(targetId, level + 1, new Set(visited)));
        }
      };
      
      if (fluxoData.etapas.length > 0) {
        calculateLevel(fluxoData.etapas[0].id, 0);
      }

      // Agrupar por nível
      const nodesByLevel = new Map();
      fluxoData.etapas.forEach(etapa => {
        const level = levels.get(etapa.id) || 0;
        if (!nodesByLevel.has(level)) nodesByLevel.set(level, []);
        nodesByLevel.get(level).push(etapa);
      });

      const newNodes = fluxoData.etapas.map((etapa, index) => {
        const level = levels.get(etapa.id) || 0;
        const nodesInLevel = nodesByLevel.get(level);
        const indexInLevel = nodesInLevel.indexOf(etapa);
        const totalInLevel = nodesInLevel.length;
        
        // Espaçamento maior para níveis com muitos nós
        const spacing = totalInLevel > 3 ? 300 : 250;
        const xOffset = (totalInLevel - 1) * spacing / 2;
        const xPos = 400 + (indexInLevel * spacing) - xOffset;
        const yPos = 100 + (level * 280);
        
        return {
          id: etapa.id || `node-${index}`,
          type: etapa.tipo_etapa,
          position: { x: xPos, y: yPos },
          data: {
            label: etapa.nome,
            mensagem: etapa.mensagem_automatica,
            opcoes: etapa.opcoes_menu,
            proxima_etapa_id: etapa.proxima_etapa_id,
            fallback_etapa_id: etapa.fallback_etapa_id,
            timeout: etapa.timeout,
            aguardar_minutos: etapa.aguardar_minutos,
            aguardar_resposta: etapa.aguardar_resposta,
            // Deriva o modo do bloco Aguardar a partir do que veio do backend.
            aguardar_modo: etapa.tipo_etapa === 'aguardar'
              ? (etapa.aguardar_minutos > 0 ? 'tempo' : 'resposta')
              : undefined,
            enviar_para_atendimento: etapa.enviar_para_atendimento || false,
            tag_atendimento_id: etapa.tag_atendimento_id || null,
            media_url: etapa.media_url || null,
            media_type: etapa.media_type || null,
            mensagem_fallback: etapa.mensagem_fallback || null,
            isInicial: index === 0,
            tipo_etapa: etapa.tipo_etapa,
          },
        };
      });

      const newEdges = [];
      fluxoData.etapas.forEach((etapa, index) => {
        // Conexões de menu
        if (etapa.tipo_etapa === 'menu' && etapa.opcoes_menu) {
          etapa.opcoes_menu.forEach((opcao, opcaoIndex) => {
            if (opcao.proxima_etapa_id) {
              newEdges.push({
                id: `e-${etapa.id}-${opcao.proxima_etapa_id}`,
                source: etapa.id,
                target: opcao.proxima_etapa_id,
                label: `${opcao.opcao}: ${opcao.texto}`,
                type: 'smoothstep',
                animated: true,
                style: { stroke: '#10b981', strokeWidth: 2 },
                labelStyle: { fill: '#10b981', fontWeight: 700, fontSize: 11 },
                labelBgStyle: { 
                  fill: darkMode ? '#18181b' : '#ffffff', 
                  fillOpacity: 1,
                  stroke: darkMode ? '#52525b' : '#d1d5db',
                  strokeWidth: 3
                },
                labelBgPadding: [10, 12],
                labelBgBorderRadius: 8,
                markerEnd: { type: MarkerType.ArrowClosed, color: '#10b981', width: 20, height: 20 },
              });
            }
          });
        }
        // Conexões manuais (proxima_etapa_id)
        if (etapa.proxima_etapa_id) {
          newEdges.push({
            id: `e-${etapa.id}-${etapa.proxima_etapa_id}`,
            source: etapa.id,
            target: etapa.proxima_etapa_id,
            type: 'smoothstep',
            animated: true,
            style: { stroke: '#3b82f6', strokeWidth: 3 },
            zIndex: 1000,
            markerEnd: { type: MarkerType.ArrowClosed, color: '#3b82f6', width: 20, height: 20 },
          });
        }
      });

      setNodes(newNodes);
      setEdges(newEdges);
    } catch (error) {
      console.error('Erro ao converter fluxo:', error);
      setAlertModal({
        isOpen: true,
        title: 'Erro ao Carregar',
        message: 'Erro ao carregar o fluxo. Verifique os dados.',
        type: 'error'
      });
    }
  };

  const aplicarTemplate = (template) => {
    // Trocar de template substitui as etapas E a declaracao de parametros.
    // Só 1 dos 4 templates declara algo, então trocar apagaria em silêncio.
    if (variaveisFluxo.length > 0 && !(template.variaveis || []).length) {
      setConfirmModal({
        isOpen: true,
        title: 'Descartar os parâmetros?',
        message: `Este template não usa parâmetros. Aplicar vai remover os ${variaveisFluxo.length} que você declarou.`,
        onConfirm: () => aplicarTemplateAgora(template),
      });
      return;
    }
    aplicarTemplateAgora(template);
  };

  const aplicarTemplateAgora = (template) => {
    setFormData({
      nome: template.nome,
      descricao: template.descricao,
      ativo: true,
    });
    // O template já vem com os parâmetros declarados: aplicar aqui é o que
    // faz o onboarding virar "preencha 5 campos" em vez de editar 11 etapas.
    setVariaveisFluxo(template.variaveis || []);
    convertFluxoToNodes({ etapas: template.etapas });
    setShowTemplates(false);
    setSelectedNode(null);
    // `fitView` como prop do ReactFlow só age no primeiro render: sem isto a
    // câmera fica no template anterior e parece que nada mudou até reabrir.
    setTimeout(() => reactFlowInstance?.fitView({ padding: 0.2, duration: 400 }), 50);
  };

  const onConnect = useCallback(
    (params) => setEdges((eds) => addEdge({ 
      ...params, 
      animated: true, 
      type: 'smoothstep',
      style: { stroke: '#3b82f6', strokeWidth: 3 },
      markerEnd: { type: MarkerType.ArrowClosed, color: '#3b82f6', width: 20, height: 20 } 
    }, eds)),
    [setEdges]
  );

  const onEdgeClick = useCallback((event, edge) => {
    event.stopPropagation();
    setConfirmModal({
      isOpen: true,
      title: 'Remover Conex\u00e3o',
      message: `Deseja remover esta conex\u00e3o?${edge.label ? `\n\n"${edge.label}"` : ''}`,
      onConfirm: () => {
        setEdges((eds) => eds.filter((e) => e.id !== edge.id));
        // Atualizar proxima_etapa_id do n\u00f3 de origem se necess\u00e1rio
        const sourceNode = nodes.find(n => n.id === edge.source);
        if (sourceNode && sourceNode.data.proxima_etapa_id === edge.target) {
          setNodes((nds) => nds.map(n => 
            n.id === edge.source 
              ? { ...n, data: { ...n.data, proxima_etapa_id: null } }
              : n
          ));
        }
      }
    });
  }, [nodes, setConfirmModal, setEdges, setNodes]);

  const onConnectStart = useCallback((_, { nodeId }) => {
    setConnectMenu({ ...connectMenu, sourceNode: nodeId });
  }, [connectMenu]);

  const onConnectEnd = useCallback((event) => {
    const targetIsPane = event.target.classList.contains('react-flow__pane');
    if (targetIsPane && connectMenu.sourceNode) {
      const { clientX, clientY } = event;
      setConnectMenu({ isOpen: true, x: clientX, y: clientY, sourceNode: connectMenu.sourceNode });
    }
  }, [connectMenu]);

  const createAndConnectNode = (type) => {
    const sourceNode = nodes.find(n => n.id === connectMenu.sourceNode);
    if (!sourceNode) return;

    const newNode = {
      id: `node-${Date.now()}`,
      type,
      position: { x: sourceNode.position.x, y: sourceNode.position.y + 250 },
      data: {
        label: `Nova Etapa`,
        tipo_etapa: type,
        mensagem: '',
        opcoes: type === 'menu' ? [] : undefined,
        isInicial: false,
      },
    };

    setNodes((nds) => [...nds, newNode]);
    
    // Atualizar proxima_etapa_id do nó de origem
    setNodes((nds) =>
      nds.map((node) =>
        node.id === connectMenu.sourceNode
          ? { ...node, data: { ...node.data, proxima_etapa_id: newNode.id } }
          : node
      )
    );
    
    setConnectMenu({ isOpen: false, x: 0, y: 0, sourceNode: null });
    setSelectedNode(newNode);
  };

  const handleUndo = () => {
    if (historyIndex.current > 0) {
      historyIndex.current -= 1;
      const prevState = history.current[historyIndex.current];
      setNodes(prevState.nodes);
      setEdges(prevState.edges);
    }
  };

  const handleRedo = () => {
    if (historyIndex.current < history.current.length - 1) {
      historyIndex.current += 1;
      const nextState = history.current[historyIndex.current];
      setNodes(nextState.nodes);
      setEdges(nextState.edges);
    }
  };

  const addNode = (type) => {
    // Encontrar posição livre
    const maxY = nodes.length > 0 ? Math.max(...nodes.map(n => n.position.y)) : 0;
    
    const newNode = {
      id: `node-${Date.now()}`,
      type,
      position: { x: 400, y: maxY + 250 },
      data: {
        label: type === 'aguardar' ? 'Aguardar' : `Nova Etapa`,
        tipo_etapa: type,
        mensagem: '',
        opcoes: type === 'menu' ? [] : undefined,
        // Bloco Aguardar: espera pura (não envia mensagem). Modo padrão = aguardar
        // resposta do usuário; se aguardar_minutos > 0, espera por tempo.
        aguardar_modo: type === 'aguardar' ? 'resposta' : undefined,
        aguardar_minutos: type === 'aguardar' ? null : undefined,
        isInicial: nodes.length === 0,
      },
    };
    setNodes((nds) => [...nds, newNode]);
    setSelectedNode(newNode);
    setTimeout(() => {
      reactFlowInstance?.setCenter(newNode.position.x, newNode.position.y + 100, { zoom: 1, duration: 800 });
    }, 100);
  };

  const validateSpecialChars = (text) => {
    const problematic = [];
    // Detectar caracteres de controle (exceto \n, \r, \t)
    if (/[\x00-\x08\x0B\x0C\x0E-\x1F\x7F]/.test(text)) {
      problematic.push('caracteres de controle inválidos');
    }
    // Detectar emojis problemáticos (surrogates órfãos)
    if (/[\uD800-\uDFFF]/.test(text.replace(/[\uD800-\uDBFF][\uDC00-\uDFFF]/g, ''))) {
      problematic.push('emojis malformados');
    }
    return problematic;
  };

  const detectCircularFlow = () => {
    // Detectar apenas loops INFINITOS (sem saída)
    // Loops válidos (ex: menu que volta ao menu) são permitidos
    const visited = new Map(); // nodeId -> count de visitas
    const MAX_VISITS = nodes.length * 2; // Limite razoável

    const hasCycle = (nodeId, depth = 0) => {
      // Limite de profundidade para evitar loops infinitos na validação
      if (depth > MAX_VISITS) return true;
      
      const node = nodes.find(n => n.id === nodeId);
      if (!node) return false;
      
      // Se é finalizar, não há loop
      if (node.type === 'finalizar') return false;
      
      // Incrementar contador de visitas
      const visitCount = (visited.get(nodeId) || 0) + 1;
      visited.set(nodeId, visitCount);
      
      // Se visitou muitas vezes, pode ser loop infinito
      if (visitCount > nodes.length) return true;
      
      let hasExit = false;
      
      // Verificar se tem pelo menos uma saída válida
      if (node.data.opcoes && node.data.opcoes.length > 0) {
        for (const opcao of node.data.opcoes) {
          if (opcao.proxima_etapa_id) {
            const targetNode = nodes.find(n => n.id === opcao.proxima_etapa_id);
            if (targetNode && targetNode.type === 'finalizar') {
              hasExit = true;
              break;
            }
            if (!hasCycle(opcao.proxima_etapa_id, depth + 1)) {
              hasExit = true;
            }
          }
        }
      }
      
      if (node.data.proxima_etapa_id) {
        const targetNode = nodes.find(n => n.id === node.data.proxima_etapa_id);
        if (targetNode && targetNode.type === 'finalizar') {
          hasExit = true;
        } else if (!hasCycle(node.data.proxima_etapa_id, depth + 1)) {
          hasExit = true;
        }
      }
      
      // Se não tem saída e não é aguardar/finalizar, é loop infinito
      if (!hasExit && node.type !== 'aguardar' && node.type !== 'finalizar') {
        return true;
      }
      
      return false;
    };

    if (nodes.length > 0) {
      return hasCycle(nodes[0].id);
    }
    return false;
  };

  const handleSave = async () => {
    try {
      console.log('Iniciando salvamento...');
      // Validações
      if (!formData.nome.trim()) {
        setAlertModal({
          isOpen: true,
          title: 'Nome Obrigatório',
          message: 'Nome do fluxo é obrigatório!',
          type: 'error'
        });
        return;
      }

      if (nodes.length === 0) {
        setAlertModal({
          isOpen: true,
          title: 'Fluxo Vazio',
          message: 'Adicione pelo menos uma etapa ao fluxo!',
          type: 'error'
        });
        return;
      }

      // Validar cada nó
      for (const node of nodes) {
        if (!node.data.label.trim()) {
          setAlertModal({
            isOpen: true,
            title: 'Nome da Etapa Obrigatório',
            message: `A etapa "${node.id}" precisa de um nome!`,
            type: 'error'
          });
          return;
        }

        if (node.type !== 'finalizar' && node.type !== 'menu' && node.type !== 'aguardar' && !node.data.mensagem?.trim()) {
          setAlertModal({
            isOpen: true,
            title: 'Mensagem Obrigatória',
            message: `A etapa "${node.data.label}" precisa de uma mensagem!`,
            type: 'error'
          });
          return;
        }

        if (node.data.mensagem && node.data.mensagem.length > 4096) {
          setAlertModal({
            isOpen: true,
            title: 'Limite de Caracteres Excedido',
            message: `A mensagem da etapa "${node.data.label}" excede o limite do WhatsApp!\n\nLimite: 4096 caracteres\nAtual: ${node.data.mensagem.length} caracteres`,
            type: 'error'
          });
          return;
        }

        if (node.data.mensagem) {
          const issues = validateSpecialChars(node.data.mensagem);
          if (issues.length > 0) {
            setAlertModal({
              isOpen: true,
              title: 'Caracteres Inválidos',
              message: `A mensagem da etapa "${node.data.label}" contém ${issues.join(', ')} que podem causar problemas no WhatsApp.\n\nRevise o texto antes de salvar.`,
              type: 'warning'
            });
            return;
          }
        }

        if (node.type === 'menu') {
          if (!node.data.opcoes || node.data.opcoes.length === 0) {
            setAlertModal({
              isOpen: true,
              title: 'Menu Sem Opções',
              message: `O menu "${node.data.label}" precisa de pelo menos uma opção!`,
              type: 'error'
            });
            return;
          }
          for (const opcao of node.data.opcoes) {
            if (!opcao.opcao.trim() || !opcao.texto.trim()) {
              setAlertModal({
                isOpen: true,
                title: 'Opção Incompleta',
                message: `Todas as opções do menu "${node.data.label}" precisam ter número e texto!`,
                type: 'error'
              });
              return;
            }
          }
        }
      }

      // Detectar loops infinitos
      if (detectCircularFlow()) {
        setAlertModal({
          isOpen: true,
          title: 'Fluxo Circular Detectado',
          message: 'Seu fluxo contém um loop infinito. Verifique as conexões.',
          type: 'warning'
        });
        return;
      }

      // Validar apenas nós sem saída que são REALMENTE problemáticos
      // Tipos aguardar e finalizar são saídas válidas
      const problematicNodes = nodes.filter(node => {
        // Finalizar e Aguardar são saídas válidas
        if (node.type === 'finalizar' || node.type === 'aguardar') return false;
        
        const hasMenuOptions = node.data.opcoes && node.data.opcoes.some(op => op.proxima_etapa_id);
        const hasProximaEtapa = node.data.proxima_etapa_id;
        
        return !hasMenuOptions && !hasProximaEtapa;
      });
      
      if (problematicNodes.length > 0) {
        const nodeNames = problematicNodes.map(n => `"${n.data.label}"`).join(', ');
        setAlertModal({
          isOpen: true,
          title: 'Atenção: Fluxo Incompleto',
          message: `As seguintes etapas não têm próxima ação: ${nodeNames}\n\nO fluxo pode parar nesses pontos sem finalizar corretamente.\n\nDica: Adicione uma próxima etapa ou mude o tipo para "Finalizar" ou "Aguardar".`,
          type: 'warning'
        });
        return;
      }

      // Validar opções de menu vazias (sem destino)
      for (const node of nodes) {
        if (node.type === 'menu' && node.data.opcoes) {
          const emptyOptions = node.data.opcoes.filter(op => !op.proxima_etapa_id);
          if (emptyOptions.length > 0) {
            const emptyOpts = emptyOptions.map(op => `"${op.opcao}: ${op.texto}"`).join(', ');
            setAlertModal({
              isOpen: true,
              title: 'Opções Sem Destino',
              message: `No menu "${node.data.label}", as seguintes opções não têm próxima etapa: ${emptyOpts}\n\nTodas as opções de menu devem ter um destino.`,
              type: 'warning'
            });
            return;
          }
        }
      }

      // Validar IDs únicos
      const nodeIds = nodes.map(n => n.id);
      const duplicateIds = nodeIds.filter((id, index) => nodeIds.indexOf(id) !== index);
      if (duplicateIds.length > 0) {
        setAlertModal({
          isOpen: true,
          title: 'IDs Duplicados',
          message: `Os seguintes IDs estão duplicados: ${[...new Set(duplicateIds)].join(', ')}\n\nCada nó deve ter um ID único.`,
          type: 'warning'
        });
        return;
      }

      // Ordenar etapas na ordem lógica do fluxo (BFS)
      const orderedNodes = [];
      const visited = new Set();
      const queue = [nodes[0].id];
      
      while (queue.length > 0) {
        const nodeId = queue.shift();
        if (visited.has(nodeId)) continue;
        
        const node = nodes.find(n => n.id === nodeId);
        if (!node) continue;
        
        visited.add(nodeId);
        orderedNodes.push(node);
        
        // Adicionar próximas etapas à fila
        if (node.data.opcoes) {
          node.data.opcoes.forEach(op => {
            if (op.proxima_etapa_id && !visited.has(op.proxima_etapa_id)) {
              queue.push(op.proxima_etapa_id);
            }
          });
        }
        if (node.data.proxima_etapa_id && !visited.has(node.data.proxima_etapa_id)) {
          queue.push(node.data.proxima_etapa_id);
        }
      }
      
      // Adicionar nós não visitados (se houver)
      nodes.forEach(node => {
        if (!visited.has(node.id)) {
          orderedNodes.push(node);
        }
      });

      const etapas = orderedNodes.map((node) => {
        const etapa = {
          id: node.id,
          nome: node.data.label,
          tipo_etapa: node.type,
          mensagem_automatica: node.type === 'aguardar' ? '' : (node.data.mensagem || ''),
          opcoes_menu: node.data.opcoes || [],
          // Bloco Aguardar (espera pura): modo 'tempo' usa aguardar_minutos e não
          // aguarda resposta; modo 'resposta' aguarda o usuário e sem minutos.
          aguardar_resposta: node.type === 'aguardar'
            ? (node.data.aguardar_modo !== 'tempo')
            : (node.data.aguardar_resposta !== false),
          aguardar_minutos: node.type === 'aguardar'
            ? (node.data.aguardar_modo === 'tempo' ? (parseInt(node.data.aguardar_minutos) || 1) : null)
            : (node.data.aguardar_minutos || null),
          enviar_para_atendimento: node.type === 'aguardar' ? false : (node.data.enviar_para_atendimento || false),
          tag_atendimento_id: node.data.tag_atendimento_id || null,
          media_url: node.type === 'aguardar' ? null : (node.data.media_url || null),
          media_type: node.type === 'aguardar' ? null : (node.data.media_type || null),
          mensagem_fallback: node.data.mensagem_fallback || null,
        };
        
        // Adicionar proxima_etapa_id das conexões manuais
        const manualEdge = edges.find(e => e.source === node.id && !e.label);
        if (manualEdge) {
          etapa.proxima_etapa_id = manualEdge.target;
        } else if (node.data.proxima_etapa_id) {
          etapa.proxima_etapa_id = node.data.proxima_etapa_id;
        }
        
        return etapa;
      });

      console.log('Salvando fluxo:', { formData, etapas });

      // Upload de mídias pendentes antes de salvar
      for (const etapa of etapas) {
        const node = orderedNodes.find(n => n.id === etapa.id);
        if (node?.data?.media_file && !etapa.media_url) {
          try {
            const uploadForm = new FormData();
            uploadForm.append('file', node.data.media_file);
            const res = await mediaAPI.upload(clienteId, user.id, uploadForm);
            if (res.data?.url) {
              etapa.media_url = res.data.url;
            } else {
              toast.error(`Erro ao fazer upload da mídia: ${etapa.nome}`);
            }
          } catch (err) {
            toast.error(`Erro ao fazer upload: ${err.message}`);
          }
        }
      }

      onSave({
        ...formData,
        etapas,
        variaveis: variaveisFluxo,
      });
      
      localStorage.removeItem('fluxo_draft');
      setHasUnsavedChanges(false);
      console.log('Fluxo salvo com sucesso!');
    } catch (error) {
      console.error('Erro ao salvar fluxo:', error);
      setAlertModal({
        isOpen: true,
        title: 'Erro ao Salvar',
        message: `Erro ao salvar o fluxo: ${error.message}`,
        type: 'error'
      });
    }
  };

  return (
    <>
      <AlertModal
        isOpen={alertModal.isOpen}
        onClose={() => setAlertModal({ ...alertModal, isOpen: false })}
        title={alertModal.title}
        message={alertModal.message}
        type={alertModal.type}
      />
      <ConfirmModal
        isOpen={confirmModal.isOpen}
        onClose={() => setConfirmModal({ ...confirmModal, isOpen: false })}
        onConfirm={confirmModal.onConfirm}
        title={confirmModal.title}
        message={confirmModal.message}
      />
      <style>{`
        .react-flow__edge-text,
        .react-flow__edge-textbg {
          z-index: 9999 !important;
        }
        .react-flow__edgelabel-renderer {
          z-index: 10000 !important;
        }
      `}</style>
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50" style={{ top: 0, left: 0, right: 0, bottom: 0, margin: 0, padding: 0 }}>
      <div className="flex items-center justify-center w-full h-full gap-4 px-4">
      <div className={`relative ${(showEditModal && selectedNode) || showPreview ? 'w-[calc(95vw-520px)] max-w-5xl' : 'w-[95vw] max-w-7xl'} h-[95vh] rounded-xl shadow-2xl overflow-hidden ${darkMode ? 'bg-gray-900' : 'bg-gray-100'}`}>
      {/* Header */}
      <div className={`${darkMode ? 'bg-zinc-800 border-b border-gray-700' : 'bg-white border-b border-gray-200'} shadow-lg`}>
        {/* Linha 1: Ações principais */}
        <div className="flex items-center justify-between px-4 py-3 border-b ${darkMode ? 'border-gray-700' : 'border-gray-200'}">
          <div className={`text-lg font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
            Editor de Fluxo
          </div>
          <div className="flex items-center space-x-2">
            <button
              title="Fechar editor (ESC)"
              onClick={() => {
                if (hasUnsavedChanges) {
                  setConfirmModal({
                    isOpen: true,
                    title: 'Alterações Não Salvas',
                    message: 'Você tem alterações não salvas!\n\nDeseja sair sem salvar?',
                    onConfirm: onCancel
                  });
                } else {
                  onCancel();
                }
              }}
              className={`secondary-button px-4 py-2 rounded-lg ${darkMode ? 'bg-zinc-700 text-white hover:bg-zinc-600' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}
            >
              <X className="w-4 h-4" />
            </button>
            <button
              onClick={handleSave}
              title="Salvar fluxo (Ctrl+S)"
              className={`gradient-button px-6 py-2 rounded-lg flex items-center space-x-2 ${
                hasUnsavedChanges 
                  ? 'bg-green-600 text-white hover:bg-green-700' 
                  : 'bg-gray-400 text-white cursor-not-allowed'
              }`}
            >
              <Save className="w-4 h-4" />
              <span>Salvar{hasUnsavedChanges ? ' *' : ''}</span>
            </button>
          </div>
        </div>
        
        {/* Linha 2: Informações e opções */}
        <div className="flex items-center justify-between px-4 py-3">
          <div className="flex-1 max-w-2xl space-y-3">
            <div>
              <label className={`block text-xs font-semibold mb-1.5 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                Nome do Fluxo *
              </label>
              <input
                type="text"
                value={formData.nome}
                onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
                className={`text-base font-medium border-2 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 w-full transition-all ${darkMode ? 'bg-zinc-700 border-zinc-600 text-white placeholder-gray-500' : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400'}`}
                placeholder="Ex: Atendimento Pizzaria"
              />
            </div>
            <div>
              <label className={`block text-xs font-semibold mb-1.5 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                Descrição
              </label>
              <input
                type="text"
                value={formData.descricao}
                onChange={(e) => setFormData({ ...formData, descricao: e.target.value })}
                className={`text-sm border-2 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 w-full transition-all ${darkMode ? 'bg-zinc-700 border-zinc-600 text-gray-300 placeholder-gray-500' : 'bg-white border-gray-300 text-gray-700 placeholder-gray-400'}`}
                placeholder="Ex: Fluxo para pedidos e reservas"
              />
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <button
              onClick={() => {
                if (nodes.length === 0) {
                  setAlertModal({ isOpen: true, title: 'Fluxo Vazio', message: 'Adicione etapas antes de testar!', type: 'error' });
                  return;
                }
                if (!showPreview) {
                  setPreviewMessages([]);
                  setPreviewCurrentNode(nodes[0]);
                  setPreviewInput('');
                  setTimeout(() => {
                    const msg = nodes[0].data.mensagem || nodes[0].data.label;
                    setPreviewMessages([{ text: msg, from: 'bot', node: nodes[0] }]);
                    if (nodes[0].data.aguardar_resposta === false && nodes[0].data.proxima_etapa_id) {
                      setTimeout(() => {
                        const nextNode = nodes.find(n => n.id === nodes[0].data.proxima_etapa_id);
                        if (nextNode) {
                          setPreviewCurrentNode(nextNode);
                          const nextMsg = nextNode.data.mensagem || nextNode.data.label;
                          setPreviewMessages(prev => [...prev, { text: nextMsg, from: 'bot', node: nextNode }]);
                        }
                      }, 800);
                    }
                  }, 300);
                }
                setShowPreview(!showPreview);
              }}
              title="Testar fluxo em tempo real"
              className={`px-3 py-1.5 text-sm rounded-lg ${showPreview ? 'bg-green-600 text-white' : darkMode ? 'bg-zinc-700 text-white hover:bg-zinc-600' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}
            >
              📱 {showPreview ? 'Fechar' : 'Testar'}
            </button>
            <button
              onClick={() => setShowVariaveis(true)}
              title="Parâmetros reaproveitáveis do fluxo"
              className={`px-3 py-1.5 text-sm rounded-lg ${
                variaveisFluxo.length > 0
                  ? 'bg-blue-600 text-white hover:bg-blue-700'
                  : darkMode ? 'bg-zinc-700 text-white hover:bg-zinc-600' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
            >
              {'{{ }}'} Parâmetros{variaveisFluxo.length > 0 ? ` (${variaveisFluxo.length})` : ''}
            </button>
            <button
              onClick={() => setShowTemplates(true)}
              title="Escolher um template pronto"
              className={`px-3 py-1.5 text-sm rounded-lg ${darkMode ? 'bg-zinc-700 text-white hover:bg-zinc-600' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}
            >
              📋 Templates
            </button>
            <button
              onClick={() => {
                const input = document.createElement('input');
                input.type = 'file';
                input.accept = '.json';
                input.onchange = (e) => {
                  const file = e.target.files[0];
                  if (file) {
                    const reader = new FileReader();
                    reader.onload = (event) => {
                      try {
                        const data = JSON.parse(event.target.result);
                        setFormData({ nome: data.nome || '', descricao: data.descricao || '', ativo: true });
                        setNodes(data.nodes || []);
                        setEdges(data.edges || []);
                        setShowTemplates(false);
                      } catch (error) {
                        setAlertModal({
                          isOpen: true,
                          title: 'Erro ao Importar',
                          message: 'Arquivo JSON inválido!',
                          type: 'error'
                        });
                      }
                    };
                    reader.readAsText(file);
                  }
                };
                input.click();
              }}
              title="Importar fluxo de JSON"
              className={`px-3 py-1.5 text-sm rounded-lg ${darkMode ? 'bg-zinc-700 text-white hover:bg-zinc-600' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}
            >
              ⬆️ Importar
            </button>
            <button
              onClick={() => {
                const data = { nome: formData.nome, descricao: formData.descricao, nodes, edges };
                const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `fluxo-${formData.nome || 'sem-nome'}.json`;
                a.click();
                URL.revokeObjectURL(url);
              }}
              title="Exportar fluxo como JSON"
              className={`px-3 py-1.5 text-sm rounded-lg ${darkMode ? 'bg-zinc-700 text-white hover:bg-zinc-600' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}
            >
              ⬇️ Exportar
            </button>
          </div>
        </div>
      </div>

      {/* Toolbar */}
      <div className={`absolute top-60 left-4 z-[5] ${darkMode ? 'bg-zinc-800 border border-gray-700' : 'bg-white border border-gray-200'} rounded-lg shadow-lg p-3 space-y-2`}>
        <div className="relative mb-2">
          <Search className={`absolute left-2 top-2 w-4 h-4 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`} />
          <input
            type="text"
            placeholder="Buscar nó..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className={`w-full pl-8 pr-3 py-1.5 text-sm rounded border ${darkMode ? 'bg-zinc-700 border-gray-600 text-white placeholder-gray-500' : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400'}`}
          />
        </div>
        {searchTerm && (
          <div className="max-h-40 overflow-y-auto space-y-1">
            {nodes.filter(n => n.data.label.toLowerCase().includes(searchTerm.toLowerCase())).map(n => (
              <button
                key={n.id}
                onClick={() => {
                  setSelectedNode(n);
                  reactFlowInstance?.setCenter(n.position.x, n.position.y + 100, { zoom: 1, duration: 800 });
                  setSearchTerm('');
                }}
                className={`w-full px-2 py-1.5 text-left text-sm rounded ${darkMode ? 'hover:bg-zinc-700 text-white' : 'hover:bg-gray-100 text-gray-900'}`}
              >
                {n.data.ordem && <span className="text-xs opacity-60">#{n.data.ordem} </span>}
                {n.data.label}
              </button>
            ))}
            {nodes.filter(n => n.data.label.toLowerCase().includes(searchTerm.toLowerCase())).length === 0 && (
              <div className={`text-xs text-center py-2 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                Nenhum nó encontrado
              </div>
            )}
          </div>
        )}
        <div className={`text-xs font-bold mb-2 px-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'} ${searchTerm ? 'mt-2 pt-2 border-t ' + (darkMode ? 'border-gray-700' : 'border-gray-200') : ''}`}>
          ADICIONAR ETAPA
        </div>
        <button
          onClick={() => addNode('mensagem')}
          title="Envia uma mensagem ao usuário"
          className={`w-full px-3 py-2 rounded flex items-center space-x-2 ${darkMode ? 'hover:bg-zinc-700 text-white' : 'hover:bg-gray-100 text-gray-900'}`}
        >
          <MessageSquare className="w-4 h-4 text-blue-500" />
          <span className="text-sm">Mensagem</span>
        </button>
        <button
          onClick={() => addNode('menu')}
          title="Apresenta opções numeradas para o usuário escolher"
          className={`w-full px-3 py-2 rounded flex items-center space-x-2 ${darkMode ? 'hover:bg-zinc-700 text-white' : 'hover:bg-gray-100 text-gray-900'}`}
        >
          <Menu className="w-4 h-4 text-green-500" />
          <span className="text-sm">Menu</span>
        </button>
        <button
          onClick={() => addNode('aguardar')}
          title="Transfere para atendimento humano"
          className={`w-full px-3 py-2 rounded flex items-center space-x-2 ${darkMode ? 'hover:bg-zinc-700 text-white' : 'hover:bg-gray-100 text-gray-900'}`}
        >
          <Clock className="w-4 h-4 text-orange-500" />
          <span className="text-sm">Aguardar</span>
        </button>
        <button
          onClick={() => addNode('finalizar')}
          title="Encerra o atendimento"
          className={`w-full px-3 py-2 rounded flex items-center space-x-2 ${darkMode ? 'hover:bg-zinc-700 text-white' : 'hover:bg-gray-100 text-gray-900'}`}
        >
          <CheckCircle className="w-4 h-4 text-red-500" />
          <span className="text-sm">Finalizar</span>
        </button>
      </div>

      {/* Templates Modal */}
      {showVariaveis && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4"
          onClick={() => setShowVariaveis(false)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className={`w-full max-w-3xl max-h-[85vh] overflow-y-auto rounded-xl shadow-2xl p-5 ${
              darkMode ? 'bg-zinc-900' : 'bg-white'
            }`}
          >
            <div className="flex items-start justify-between mb-3">
              <div>
                <h3 className={`text-base font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                  Parâmetros do fluxo
                </h3>
                <p className={`text-xs mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  Declare o que muda entre um uso e outro para reaproveitar o mesmo
                  fluxo em vários canais. O valor de cada canal é preenchido em
                  Configurações › Canais.
                </p>
              </div>
              <button
                onClick={() => setShowVariaveis(false)}
                className={`px-3 py-1.5 text-sm rounded-lg shrink-0 ${
                  darkMode ? 'bg-zinc-700 text-white hover:bg-zinc-600' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                Fechar
              </button>
            </div>

            <PainelVariaveis
              variaveis={variaveisFluxo}
              onChange={setVariaveisFluxo}
              darkMode={darkMode}
            />

            <p className={`text-[11px] mt-3 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
              As alterações entram junto com o fluxo ao clicar em Salvar.
            </p>
          </div>
        </div>
      )}

      {showTemplates && (
        <div className="absolute inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className={`max-w-3xl w-full max-h-[85vh] overflow-y-auto mx-4 p-6 rounded-lg ${darkMode ? 'bg-zinc-800' : 'bg-white'}`}>
            <div className="flex justify-between items-center mb-6">
              <div>
                <h3 className={`text-xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                  Escolha um Template
                </h3>
                <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                  Comece com um modelo pronto ou crie do zero
                </p>
              </div>
              <button
                onClick={() => setShowTemplates(false)}
                className={`p-2 rounded ${darkMode ? 'hover:bg-zinc-700' : 'hover:bg-gray-100'}`}
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <button
                onClick={() => setShowTemplates(false)}
                className={`p-6 border-2 border-dashed rounded-lg text-left transition-all ${darkMode ? 'border-gray-600 hover:border-blue-500 hover:bg-zinc-700' : 'border-gray-300 hover:border-blue-500 hover:bg-blue-50'}`}
              >
                <Plus className={`w-8 h-8 mb-3 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`} />
                <h4 className={`font-semibold mb-1 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                  Criar do Zero
                </h4>
                <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                  Comece com um fluxo vazio
                </p>
              </button>

              {templates.map((template) => (
                <button
                  key={template.id}
                  onClick={() => aplicarTemplate(template)}
                  className={`p-6 border-2 rounded-lg text-left transition-all ${darkMode ? 'border-gray-700 hover:border-green-500 hover:bg-zinc-700' : 'border-gray-200 hover:border-green-500 hover:bg-green-50'}`}
                >
                  <h4 className={`font-semibold mb-1 text-lg ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                    {template.nome}
                  </h4>
                  <p className={`text-sm mb-2 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                    {template.descricao}
                  </p>
                  <span className={`text-xs ${darkMode ? 'text-gray-500' : 'text-gray-500'}`}>
                    {template.etapas.length} etapas
                  </span>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* React Flow Canvas */}
      <div className="h-[calc(100%-132px)] relative">
        <ReactFlow
          nodes={nodes}
          edges={edges}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          onConnect={onConnect}
          onConnectStart={onConnectStart}
          onConnectEnd={onConnectEnd}
          onEdgeClick={onEdgeClick}
          nodeTypes={nodeTypes}
          onNodeClick={(_, node) => {
            setSelectedNode(node);
            setShowPreview(false);
            setShowEditModal(true);
            reactFlowInstance?.setCenter(node.position.x, node.position.y + 100, { zoom: 1, duration: 800 });
          }}
          fitView
          defaultZoom={0.7}
          minZoom={0.3}
          maxZoom={1.5}
          defaultEdgeOptions={{
            type: 'smoothstep',
            animated: true,
            style: { strokeWidth: 3 },
          }}
          className={darkMode ? 'bg-zinc-900' : 'bg-gray-50'}
        >
          <Controls className={darkMode ? 'bg-zinc-800 border-gray-700' : ''} />
          <MiniMap 
            className={darkMode ? 'bg-zinc-800' : ''}
            nodeColor={(node) => {
              switch (node.type) {
                case 'mensagem': return '#3b82f6';
                case 'menu': return '#10b981';
                case 'aguardar': return '#f97316';
                case 'finalizar': return '#ef4444';
                default: return '#6b7280';
              }
            }}
          />
          <Background color={darkMode ? '#444' : '#aaa'} gap={20} size={1} />
        </ReactFlow>
      </div>

      {/* Connect Menu */}
      {connectMenu.isOpen && (
        <>
          <div 
            className="fixed inset-0 z-40" 
            onClick={() => setConnectMenu({ isOpen: false, x: 0, y: 0, sourceNode: null })}
          />
          <div 
            className={`fixed z-50 ${darkMode ? 'bg-zinc-800 border border-gray-700' : 'bg-white border border-gray-200'} rounded-lg shadow-2xl p-2 min-w-[160px]`}
            style={{ left: connectMenu.x, top: connectMenu.y }}
          >
            <div className={`text-xs font-semibold mb-2 px-2 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
              CRIAR E CONECTAR
            </div>
            <button
              onClick={() => createAndConnectNode('mensagem')}
              className={`w-full px-3 py-2 rounded flex items-center space-x-2 ${darkMode ? 'hover:bg-zinc-700 text-white' : 'hover:bg-gray-100 text-gray-900'}`}
            >
              <MessageSquare className="w-4 h-4 text-blue-500" />
              <span className="text-sm">Mensagem</span>
            </button>
            <button
              onClick={() => createAndConnectNode('menu')}
              className={`w-full px-3 py-2 rounded flex items-center space-x-2 ${darkMode ? 'hover:bg-zinc-700 text-white' : 'hover:bg-gray-100 text-gray-900'}`}
            >
              <Menu className="w-4 h-4 text-green-500" />
              <span className="text-sm">Menu</span>
            </button>
            <button
              onClick={() => createAndConnectNode('aguardar')}
              className={`w-full px-3 py-2 rounded flex items-center space-x-2 ${darkMode ? 'hover:bg-zinc-700 text-white' : 'hover:bg-gray-100 text-gray-900'}`}
            >
              <Clock className="w-4 h-4 text-orange-500" />
              <span className="text-sm">Aguardar</span>
            </button>
            <button
              onClick={() => createAndConnectNode('finalizar')}
              className={`w-full px-3 py-2 rounded flex items-center space-x-2 ${darkMode ? 'hover:bg-zinc-700 text-white' : 'hover:bg-gray-100 text-gray-900'}`}
            >
              <CheckCircle className="w-4 h-4 text-red-500" />
              <span className="text-sm">Finalizar</span>
            </button>
          </div>
        </>
      )}

      </div>

      {/* Preview Panel */}
      {showPreview && (
        <div className={`w-[500px] h-[95vh] rounded-xl shadow-2xl overflow-hidden ${darkMode ? 'bg-zinc-800 border border-gray-700' : 'bg-white border border-gray-200'}`}>
          <div className={`flex items-center justify-between px-4 py-3 border-b ${darkMode ? 'border-gray-700 bg-zinc-900' : 'border-gray-200 bg-gray-50'}`}>
            <h3 className={`font-semibold text-base ${darkMode ? 'text-white' : 'text-gray-900'}`}>
              📱 Simulador WhatsApp
            </h3>
            <div className="flex items-center gap-2">
              <button
                onClick={() => {
                  setPreviewMessages([]);
                  setPreviewCurrentNode(nodes[0]);
                  setPreviewInput('');
                  setTimeout(() => {
                    const msg = nodes[0].data.mensagem || nodes[0].data.label;
                    setPreviewMessages([{ text: msg, from: 'bot', node: nodes[0] }]);
                  }, 300);
                }}
                className={`px-3 py-1.5 text-sm rounded-lg transition-colors ${darkMode ? 'bg-zinc-700 hover:bg-zinc-600 text-white' : 'bg-gray-200 hover:bg-gray-300 text-gray-700'}`}
              >
                🔄 Reiniciar
              </button>
              <button
                onClick={() => {
                  setShowPreview(false);
                  setPreviewMessages([]);
                  setPreviewCurrentNode(null);
                  setPreviewInput('');
                }}
                className={`p-1.5 rounded-lg transition-colors ${darkMode ? 'text-gray-400 hover:text-white hover:bg-zinc-800' : 'text-gray-600 hover:text-gray-900 hover:bg-gray-200'}`}
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>
          <div className="flex flex-col h-[calc(100%-3.5rem)]">
            <div className={`flex-1 p-4 overflow-y-auto ${darkMode ? 'bg-[#0b141a]' : 'bg-[#e5ddd5]'}`} style={{ backgroundImage: darkMode ? 'none' : 'url("data:image/svg+xml,%3Csvg width="100" height="100" xmlns="http://www.w3.org/2000/svg"%3E%3Cpath d="M0 0h100v100H0z" fill="%23e5ddd5"/%3E%3Cpath d="M20 10h60v2H20zm0 20h40v2H20zm0 20h50v2H20z" fill="%23d1c7b7" opacity=".1"/%3E%3C/svg%3E")' }}>
              <div className="space-y-2">
                {previewMessages.map((msg, idx) => {
                  const formattedMsg = resolverPreview(msg.text, variaveisFluxo);
                  
                  return (
                    <div key={idx} className={`flex ${msg.from === 'user' ? 'justify-end' : 'justify-start'}`}>
                      <div className={`relative max-w-[75%] ${msg.from === 'user' ? 'ml-auto' : ''}`}>
                        <div className={`rounded-lg p-3 shadow-sm relative ${msg.from === 'user' ? 'bg-[#DCF8C6]' : 'bg-white'}`}>
                          <div className="text-sm whitespace-pre-wrap break-words" style={{ wordBreak: 'break-word', color: '#000' }}>
                            {formattedMsg.split(/([*_~])([^*_~]+)\1/g).map((part, i) => {
                              if (i % 3 === 1) {
                                const marker = part;
                                const text = formattedMsg.split(/([*_~])([^*_~]+)\1/g)[i + 1];
                                if (marker === '*') return <strong key={i}>{text}</strong>;
                                if (marker === '_') return <em key={i}>{text}</em>;
                                if (marker === '~') return <s key={i}>{text}</s>;
                              }
                              if (i % 3 === 0) return part;
                              return null;
                            })}
                          </div>
                          <div className="text-[10px] text-gray-600 text-right mt-1">
                            {new Date().toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
                          </div>

                        </div>
                      </div>
                    </div>
                  );
                })}
                <div ref={previewEndRef} />
              </div>
            </div>
            <div className={`p-3 border-t ${darkMode ? 'border-gray-700 bg-zinc-800' : 'border-gray-200 bg-[#f0f0f0]'}`}>
              {previewCurrentNode?.type === 'finalizar' ? (
                <div className={`text-center py-4 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                  Conversa finalizada
                </div>
              ) : (
                <form
                  onSubmit={(e) => {
                    e.preventDefault();
                    if (!previewInput.trim()) return;
                    
                    setPreviewMessages(prev => [...prev, { text: previewInput, from: 'user' }]);
                    const userInput = previewInput;
                    setPreviewInput('');
                    
                    setTimeout(() => {
                      if (previewCurrentNode?.type === 'menu' && previewCurrentNode?.data.opcoes) {
                        const opcao = previewCurrentNode.data.opcoes.find(op => op.opcao === userInput.trim());
                        if (opcao?.proxima_etapa_id) {
                          const nextNode = nodes.find(n => n.id === opcao.proxima_etapa_id);
                          if (nextNode) {
                            setPreviewCurrentNode(nextNode);
                            const msg = nextNode.data.mensagem || nextNode.data.label;
                            setPreviewMessages(prev => [...prev, { text: msg, from: 'bot', node: nextNode }]);
                            
                            if (nextNode.data.aguardar_resposta === false && nextNode.data.proxima_etapa_id) {
                              setTimeout(() => {
                                const nextNode2 = nodes.find(n => n.id === nextNode.data.proxima_etapa_id);
                                if (nextNode2) {
                                  setPreviewCurrentNode(nextNode2);
                                  const msg2 = nextNode2.data.mensagem || nextNode2.data.label;
                                  setPreviewMessages(prev => [...prev, { text: msg2, from: 'bot', node: nextNode2 }]);
                                }
                              }, 800);
                            }
                          }
                        } else {
                          setPreviewMessages(prev => [...prev, { text: 'Opção inválida. Tente novamente.', from: 'bot' }]);
                        }
                      } else if (previewCurrentNode?.data.proxima_etapa_id) {
                        const nextNode = nodes.find(n => n.id === previewCurrentNode.data.proxima_etapa_id);
                        if (nextNode) {
                          setPreviewCurrentNode(nextNode);
                          const msg = nextNode.data.mensagem || nextNode.data.label;
                          setPreviewMessages(prev => [...prev, { text: msg, from: 'bot', node: nextNode }]);
                          
                          if (nextNode.data.aguardar_resposta === false && nextNode.data.proxima_etapa_id) {
                            setTimeout(() => {
                              const nextNode2 = nodes.find(n => n.id === nextNode.data.proxima_etapa_id);
                              if (nextNode2) {
                                setPreviewCurrentNode(nextNode2);
                                const msg2 = nextNode2.data.mensagem || nextNode2.data.label;
                                setPreviewMessages(prev => [...prev, { text: msg2, from: 'bot', node: nextNode2 }]);
                              }
                            }, 800);
                          }
                        }
                      }
                    }, 500);
                  }}
                  className="flex gap-2"
                >
                  <input
                    type="text"
                    value={previewInput}
                    onChange={(e) => setPreviewInput(e.target.value)}
                    placeholder="Digite sua mensagem..."
                    className={`flex-1 px-4 py-3 rounded-full border-2 focus:outline-none focus:border-green-500 ${darkMode ? 'bg-zinc-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
                  />
                  <button
                    type="submit"
                    className="px-5 py-3 rounded-full bg-green-600 hover:bg-green-700 text-white font-medium"
                  >
                    ➤
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Edit Panel */}
      {showEditModal && selectedNode && !showPreview && (
        <div className={`w-[500px] h-[95vh] rounded-xl shadow-2xl overflow-hidden ${darkMode ? 'bg-zinc-800 border border-gray-700' : 'bg-white border border-gray-200'}`}>
          <div className={`flex items-center justify-between px-4 py-3 border-b ${darkMode ? 'border-gray-700 bg-zinc-900' : 'border-gray-200 bg-gray-50'}`}>
            <h3 className={`font-semibold text-base ${darkMode ? 'text-white' : 'text-gray-900'}`}>
              Editar: {selectedNode.data.label}
            </h3>
            <button
              onClick={() => setShowEditModal(false)}
              className={`p-1.5 rounded-lg transition-colors ${darkMode ? 'text-gray-400 hover:text-white hover:bg-zinc-800' : 'text-gray-600 hover:text-gray-900 hover:bg-gray-200'}`}
            >
              <X className="w-4 h-4" />
            </button>
          </div>
          <div className="flex flex-col h-[calc(100%-3.5rem)]">
            <div className={`flex-1 p-4 overflow-y-auto`}>
                <div className="space-y-4">
            <div>
              <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                Nome
              </label>
              <input
                type="text"
                value={selectedNode.data.label}
                onChange={(e) => {
                  setNodes((nds) =>
                    nds.map((node) =>
                      node.id === selectedNode.id
                        ? { ...node, data: { ...node.data, label: e.target.value } }
                        : node
                    )
                  );
                }}
                className={`w-full px-3 py-2 rounded border ${
                  darkMode
                    ? 'bg-zinc-700 border-gray-600 text-white'
                    : 'bg-white border-gray-300 text-gray-900'
                }`}
              />
            </div>
            {selectedNode.type !== 'finalizar' && selectedNode.type !== 'aguardar' && (
              <div>
                <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  Mensagem
                </label>
                <div className={`flex gap-2 mb-3 p-3 rounded-lg border ${darkMode ? 'bg-zinc-700 border-gray-600' : 'bg-gray-50 border-gray-300'}`}>
                  <button
                    onClick={() => {
                      const textarea = textareaRef.current;
                      if (!textarea) return;
                      const start = textarea.selectionStart;
                      const end = textarea.selectionEnd;
                      const text = selectedNode.data.mensagem || '';
                      const selected = text.substring(start, end) || 'texto';
                      const newText = text.substring(0, start) + '*' + selected + '*' + text.substring(end);
                      setNodes((nds) => nds.map((node) => node.id === selectedNode.id ? { ...node, data: { ...node.data, mensagem: newText } } : node));
                    }}
                    title="Negrito (*texto*)"
                    className={`px-4 py-2 text-sm font-bold rounded-lg transition-colors ${darkMode ? 'bg-zinc-600 hover:bg-zinc-500 text-white' : 'bg-white hover:bg-gray-200 text-gray-900 shadow-sm'}`}
                  >
                    *B*
                  </button>
                  <button
                    onClick={() => {
                      const textarea = textareaRef.current;
                      if (!textarea) return;
                      const start = textarea.selectionStart;
                      const end = textarea.selectionEnd;
                      const text = selectedNode.data.mensagem || '';
                      const selected = text.substring(start, end) || 'texto';
                      const newText = text.substring(0, start) + '_' + selected + '_' + text.substring(end);
                      setNodes((nds) => nds.map((node) => node.id === selectedNode.id ? { ...node, data: { ...node.data, mensagem: newText } } : node));
                    }}
                    title="Itálico (_texto_)"
                    className={`px-4 py-2 text-sm italic rounded-lg transition-colors ${darkMode ? 'bg-zinc-600 hover:bg-zinc-500 text-white' : 'bg-white hover:bg-gray-200 text-gray-900 shadow-sm'}`}
                  >
                    _I_
                  </button>
                  <button
                    onClick={() => {
                      const textarea = textareaRef.current;
                      if (!textarea) return;
                      const start = textarea.selectionStart;
                      const end = textarea.selectionEnd;
                      const text = selectedNode.data.mensagem || '';
                      const selected = text.substring(start, end) || 'texto';
                      const newText = text.substring(0, start) + '~' + selected + '~' + text.substring(end);
                      setNodes((nds) => nds.map((node) => node.id === selectedNode.id ? { ...node, data: { ...node.data, mensagem: newText } } : node));
                    }}
                    title="Tachado (~texto~)"
                    className={`px-4 py-2 text-sm line-through rounded-lg transition-colors ${darkMode ? 'bg-zinc-600 hover:bg-zinc-500 text-white' : 'bg-white hover:bg-gray-200 text-gray-900 shadow-sm'}`}
                  >
                    ~S~
                  </button>
                  <button
                    onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                    title="Emojis"
                    className={`px-4 py-2 text-lg rounded-lg transition-colors ${darkMode ? 'bg-zinc-600 hover:bg-zinc-500 text-white' : 'bg-white hover:bg-gray-200 text-gray-900 shadow-sm'}`}
                  >
                    😊
                  </button>
                </div>
                {showEmojiPicker && (
                  <div className="mb-2 relative">
                    <div ref={emojiPickerRef} className="absolute right-0 top-0 z-50">
                      <EmojiPicker
                        onEmojiClick={(emojiData) => {
                          const text = selectedNode.data.mensagem || '';
                          setNodes((nds) => nds.map((node) => node.id === selectedNode.id ? { ...node, data: { ...node.data, mensagem: text + emojiData.emoji } } : node));
                        }}
                        theme={darkMode ? 'dark' : 'light'}
                        width="350px"
                        height="400px"
                      />
                    </div>
                  </div>
                )}
                <textarea
                  ref={textareaRef}
                  value={selectedNode.data.mensagem || ''}
                  onChange={(e) => {
                    setNodes((nds) =>
                      nds.map((node) =>
                        node.id === selectedNode.id
                          ? { ...node, data: { ...node.data, mensagem: e.target.value } }
                          : node
                      )
                    );
                  }}
                  rows={8}
                  className={`w-full px-4 py-3 rounded-lg border-2 text-base leading-relaxed ${
                    darkMode
                      ? 'bg-zinc-700 border-gray-600 text-white focus:border-blue-500'
                      : 'bg-white border-gray-300 text-gray-900 focus:border-blue-500'
                  } focus:outline-none focus:ring-2 focus:ring-blue-500/20 transition-all`}
                />
                <div className={`text-xs mt-1 ${(selectedNode.data.mensagem?.length || 0) > 4096 ? 'text-red-500' : darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                  {selectedNode.data.mensagem?.length || 0} / 4096 caracteres
                </div>

                {/* Mídia - junto ao editor de mensagem */}
                <div className={`mt-3 p-3 rounded-lg border ${darkMode ? 'bg-zinc-800 border-gray-700' : 'bg-gray-50 border-gray-200'}`}>
                  <div className="flex gap-2 items-center flex-wrap">
                    <span className={`text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>📎 Mídia:</span>
                    <select
                      value={selectedNode.data.media_type || ''}
                      onChange={(e) => {
                        setNodes((nds) => nds.map((node) => node.id === selectedNode.id ? { ...node, data: { ...node.data, media_type: e.target.value || null, media_url: e.target.value ? node.data.media_url : null, media_file: e.target.value ? node.data.media_file : null, media_preview: e.target.value ? node.data.media_preview : null } } : node));
                      }}
                      className={`rounded px-2 py-1 text-sm ${darkMode ? 'bg-zinc-700 text-white border-gray-600' : 'bg-white text-gray-900 border-gray-300'} border`}
                    >
                      <option value="">Nenhuma</option>
                      <option value="image">Imagem</option>
                      <option value="video">Vídeo</option>
                      <option value="document">Documento</option>
                      <option value="audio">Áudio</option>
                    </select>
                    {selectedNode.data.media_type && !selectedNode.data.media_url && !selectedNode.data.media_preview && (
                      <input
                        type="file"
                        accept={
                          selectedNode.data.media_type === 'image' ? 'image/*' :
                          selectedNode.data.media_type === 'video' ? 'video/*' :
                          selectedNode.data.media_type === 'audio' ? 'audio/*' :
                          '.pdf,.doc,.docx,.xls,.xlsx'
                        }
                        onChange={(e) => {
                          const file = e.target.files[0];
                          if (!file) return;
                          // Bloqueia AQUI, na escolha do arquivo: acima do limite da
                          // Meta a mensagem sairia como "enviada" e não chegaria.
                          const v = validarTamanhoMidia(selectedNode.data.media_type || 'image', file.size);
                          if (!v.ok) {
                            toast.error(v.mensagem);
                            e.target.value = '';
                            return;
                          }
                          const preview = URL.createObjectURL(file);
                          setNodes((nds) => nds.map((node) => node.id === selectedNode.id ? { ...node, data: { ...node.data, media_file: file, media_preview: preview } } : node));
                        }}
                        className={`text-xs ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}
                      />
                    )}
                    {(selectedNode.data.media_preview || selectedNode.data.media_url) && (
                      <button
                        onClick={() => {
                          if (selectedNode.data.media_preview) URL.revokeObjectURL(selectedNode.data.media_preview);
                          setNodes((nds) => nds.map((node) => node.id === selectedNode.id ? { ...node, data: { ...node.data, media_url: null, media_type: null, media_file: null, media_preview: null } } : node));
                        }}
                        className="text-red-500 hover:text-red-600 text-xs"
                      >✕ Remover</button>
                    )}
                  </div>
                </div>

                {(selectedNode.data.mensagem || selectedNode.data.media_preview || selectedNode.data.media_url) && (
                  <div className={`mt-3 p-4 rounded-lg ${darkMode ? 'bg-zinc-800' : 'bg-gray-100'}`}>
                    <div className={`text-sm font-semibold mb-3 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>📱 Preview WhatsApp</div>
                    <div className="relative max-w-[85%]">
                      <div className="bg-[#DCF8C6] text-gray-900 rounded-lg shadow-sm relative overflow-hidden">
                        {(selectedNode.data.media_preview || selectedNode.data.media_url) && selectedNode.data.media_type === 'image' && (
                          <img src={selectedNode.data.media_preview || selectedNode.data.media_url} alt="Preview" className="w-full rounded-t-lg" />
                        )}
                        {(selectedNode.data.media_preview || selectedNode.data.media_url) && selectedNode.data.media_type === 'video' && (
                          <video src={selectedNode.data.media_preview || selectedNode.data.media_url} controls className="w-full max-h-48" />
                        )}
                        {(selectedNode.data.media_preview || selectedNode.data.media_url) && selectedNode.data.media_type === 'audio' && (
                          <div className="p-2 bg-[#c8e6c9]">
                            <audio src={selectedNode.data.media_preview || selectedNode.data.media_url} controls className="w-full h-8" />
                          </div>
                        )}
                        {(selectedNode.data.media_preview || selectedNode.data.media_url) && selectedNode.data.media_type === 'document' && (
                          <div className="p-2 bg-[#c8e6c9] flex items-center gap-2">
                            <span>📄</span>
                            <span className="text-sm">{selectedNode.data.media_file?.name || 'Documento'}</span>
                          </div>
                        )}
                        <div className="p-3">
                          <div className="text-sm whitespace-pre-wrap break-words" style={{ wordBreak: 'break-word' }}>
                            {resolverPreview(selectedNode.data.mensagem, variaveisFluxo)
                            .split(/([*_~])([^*_~]+)\1/g)
                            .map((part, i) => {
                              if (i % 3 === 1) {
                                const marker = part;
                                const text = resolverPreview(selectedNode.data.mensagem, variaveisFluxo).split(/([*_~])([^*_~]+)\1/g)[i + 1];
                                if (marker === '*') return <strong key={i}>{text}</strong>;
                                if (marker === '_') return <em key={i}>{text}</em>;
                                if (marker === '~') return <s key={i}>{text}</s>;
                              }
                              if (i % 3 === 0) return part;
                              return null;
                            })}
                        </div>
                        <div className="text-[10px] text-gray-600 text-right mt-1">
                          {new Date().toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
                        </div>
                        </div>
                        <div className="absolute bottom-0 right-[-8px] w-0 h-0 border-l-[8px] border-l-[#DCF8C6] border-b-[8px] border-b-transparent"></div>
                      </div>
                    </div>
                  </div>
                )}
                {/* Antes eram 2 variáveis hardcoded; o MessageVariables traz a
                    lista real do backend e insere na posição do cursor. */}
                <MessageVariables
                  // No fluxo, `usuario_nome` e `cliente_nome` nao tem quem os
                  // popular: o backend as apagaria da mensagem enviada.
                  ocultar={['usuario_nome', 'cliente_nome']}
                  textareaRef={textareaRef}
                  onInsertVariable={(novoTexto) => {
                    setNodes((nds) =>
                      nds.map((node) =>
                        node.id === selectedNode.id
                          ? { ...node, data: { ...node.data, mensagem: novoTexto } }
                          : node
                      )
                    );
                  }}
                />

                {/* Parâmetros do fluxo: é o que permite um fluxo servir vários
                    canais em vez de virar N cópias. */}
                {variaveisFluxo.length > 0 && (
                  <div className={`text-xs mt-2 p-2 rounded ${darkMode ? 'bg-zinc-700' : 'bg-gray-100'}`}>
                    <div className={`font-semibold mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      Parâmetros deste fluxo:
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                      {variaveisFluxo.map((v) => (
                        <button
                          key={v.chave}
                          type="button"
                          onClick={() => inserirNoCursor(`{{${v.chave}}}`)}
                          title={v.label || v.chave}
                          className={`px-2 py-1 rounded font-mono text-[11px] ${
                            darkMode ? 'bg-zinc-600 text-blue-300 hover:bg-zinc-500' : 'bg-white text-blue-600 hover:bg-gray-50 border border-gray-200'
                          }`}
                        >
                          {'{{' + v.chave + '}}'}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
            {selectedNode.type === 'aguardar' && (
              <div>
                <div className={`text-xs mb-2 px-2 py-1.5 rounded ${darkMode ? 'bg-zinc-700 text-gray-300' : 'bg-orange-50 text-orange-700'}`}>
                  ⏱️ Bloco de espera — não envia mensagem. Só aguarda e segue para a próxima etapa.
                </div>
                <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  Modo de espera
                </label>
                <select
                  value={selectedNode.data.aguardar_modo || 'resposta'}
                  onChange={(e) => {
                    const modo = e.target.value;
                    setNodes((nds) => nds.map((node) =>
                      node.id === selectedNode.id
                        ? { ...node, data: { ...node.data, aguardar_modo: modo } }
                        : node
                    ));
                    setSelectedNode((sn) => sn ? { ...sn, data: { ...sn.data, aguardar_modo: modo } } : sn);
                  }}
                  className={`w-full px-3 py-2 rounded border ${darkMode ? 'bg-zinc-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
                >
                  <option value="resposta">Aguardar o usuário responder</option>
                  <option value="tempo">Aguardar um tempo definido</option>
                </select>

                {(selectedNode.data.aguardar_modo || 'resposta') === 'tempo' && (
                  <div className="mt-3">
                    <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      Esperar (minutos)
                    </label>
                    <input
                      type="number"
                      min="1"
                      value={selectedNode.data.aguardar_minutos || ''}
                      placeholder="ex: 10"
                      onChange={(e) => {
                        const min = e.target.value;
                        setNodes((nds) => nds.map((node) =>
                          node.id === selectedNode.id
                            ? { ...node, data: { ...node.data, aguardar_minutos: min } }
                            : node
                        ));
                        setSelectedNode((sn) => sn ? { ...sn, data: { ...sn.data, aguardar_minutos: min } } : sn);
                      }}
                      className={`w-full px-3 py-2 rounded border ${darkMode ? 'bg-zinc-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
                    />
                    <p className={`text-[11px] mt-1 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                      Após esse tempo, o fluxo segue sozinho para a próxima etapa.
                    </p>
                  </div>
                )}

                <label className={`block text-sm font-medium mt-3 mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  Próxima etapa
                </label>
                <select
                  value={selectedNode.data.proxima_etapa_id || ''}
                  onChange={(e) => {
                    setNodes((nds) =>
                      nds.map((node) =>
                        node.id === selectedNode.id
                          ? { ...node, data: { ...node.data, proxima_etapa_id: e.target.value || null } }
                          : node
                      )
                    );
                  }}
                  className={`w-full px-3 py-2 rounded border ${
                    darkMode
                      ? 'bg-zinc-700 border-gray-600 text-white'
                      : 'bg-white border-gray-300 text-gray-900'
                  }`}
                >
                  <option value="">Nenhuma (finalizar)</option>
                  {nodes.filter(n => n.id !== selectedNode.id).map(n => (
                    <option key={n.id} value={n.id}>{n.data.label}</option>
                  ))}
                </select>
              </div>
            )}
            {selectedNode.type === 'mensagem' && (
              <div>
                <label className={`flex items-center space-x-2 cursor-pointer`}>
                  <input
                    type="checkbox"
                    checked={selectedNode.data.aguardar_resposta !== false}
                    onChange={(e) => {
                      setNodes((nds) =>
                        nds.map((node) =>
                          node.id === selectedNode.id
                            ? { ...node, data: { ...node.data, aguardar_resposta: e.target.checked } }
                            : node
                        )
                      );
                    }}
                    className="rounded"
                  />
                  <span className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    Aguardar resposta do usuário
                  </span>
                </label>
                {/* Intervalo antes de enviar esta etapa. Só faz sentido na cadeia
                    automática: se aguarda resposta, quem dita o tempo é a pessoa. */}
                {selectedNode.data.aguardar_resposta === false && (
                  <div className="mt-3">
                    <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      Aguardar antes de enviar (minutos)
                    </label>
                    <input
                      type="number"
                      min="0"
                      placeholder="Ex: 60 (1 hora). Vazio = envia na hora"
                      value={selectedNode.data.aguardar_minutos || ''}
                      onChange={(e) => {
                        setNodes((nds) =>
                          nds.map((node) =>
                            node.id === selectedNode.id
                              ? { ...node, data: { ...node.data, aguardar_minutos: e.target.value ? parseInt(e.target.value) : null } }
                              : node
                          )
                        );
                      }}
                      className={`w-full px-3 py-2 rounded border ${
                        darkMode ? 'bg-zinc-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'
                      }`}
                    />
                    <div className={`text-xs mt-1 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                      Espera este tempo depois da mensagem anterior antes de enviar esta.
                    </div>
                  </div>
                )}
                <label className={`block text-sm font-medium mt-3 mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  Próxima etapa
                </label>
                <select
                  value={selectedNode.data.proxima_etapa_id || ''}
                  onChange={(e) => {
                    setNodes((nds) =>
                      nds.map((node) =>
                        node.id === selectedNode.id
                          ? { ...node, data: { ...node.data, proxima_etapa_id: e.target.value || null } }
                          : node
                      )
                    );
                  }}
                  className={`w-full px-3 py-2 rounded border ${
                    darkMode
                      ? 'bg-zinc-700 border-gray-600 text-white'
                      : 'bg-white border-gray-300 text-gray-900'
                  }`}
                >
                  <option value="">Nenhuma (finalizar)</option>
                  {nodes.filter(n => n.id !== selectedNode.id).map(n => (
                    <option key={n.id} value={n.id}>{n.data.label}</option>
                  ))}
                </select>
              </div>
            )}
            {selectedNode.type === 'menu' && (
              <div>
                <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  Opções do Menu
                </label>
                {/* Deixa claro o comportamento da Meta: o formato do menu muda com
                    o número de opções, e a pessoa que monta o fluxo precisa saber. */}
                <div className={`mb-2 text-xs rounded p-2 ${darkMode ? 'bg-zinc-800 text-gray-400' : 'bg-blue-50 text-blue-700'}`}>
                  {(selectedNode.data.opcoes || []).length <= 3
                    ? 'Até 3 opções aparecem como botões no WhatsApp.'
                    : 'Acima de 3 opções o WhatsApp mostra como lista (menu "Ver opções"), não botões. Máximo de 10.'}
                  {(selectedNode.data.media_url || selectedNode.data.media_file) && (selectedNode.data.opcoes || []).length > 3 && (
                    <span className="block mt-1 text-amber-500">
                      ⚠️ Com imagem e mais de 3 opções, a imagem é enviada em uma mensagem separada antes da lista (a lista não exibe imagem).
                    </span>
                  )}
                </div>
                {(selectedNode.data.opcoes || []).map((opcao, index) => (
                  <div key={index} className={`mb-2 p-2 rounded border ${darkMode ? 'border-gray-600' : 'border-gray-300'}`}>
                    <div className="flex items-center justify-between mb-1">
                      <span className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Opção {index + 1}</span>
                      <button
                        onClick={() => {
                          const newOpcoes = (selectedNode.data.opcoes || []).filter((_, i) => i !== index);
                          setNodes((nds) =>
                            nds.map((node) =>
                              node.id === selectedNode.id
                                ? { ...node, data: { ...node.data, opcoes: newOpcoes } }
                                : node
                            )
                          );
                        }}
                        className="text-red-500 hover:text-red-700"
                      >
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </div>
                    <input
                      type="text"
                      placeholder="Opção (ex: 1)"
                      value={opcao.opcao}
                      onChange={(e) => {
                        const newOpcoes = [...(selectedNode.data.opcoes || [])];
                        newOpcoes[index] = { ...newOpcoes[index], opcao: e.target.value };
                        setNodes((nds) =>
                          nds.map((node) =>
                            node.id === selectedNode.id
                              ? { ...node, data: { ...node.data, opcoes: newOpcoes } }
                              : node
                          )
                        );
                      }}
                      className={`w-full px-2 py-1 text-sm rounded border mb-1 ${
                        darkMode
                          ? 'bg-zinc-700 border-gray-600 text-white'
                          : 'bg-white border-gray-300 text-gray-900'
                      }`}
                    />
                    <input
                      type="text"
                      placeholder="Texto"
                      value={opcao.texto}
                      onChange={(e) => {
                        const newOpcoes = [...(selectedNode.data.opcoes || [])];
                        newOpcoes[index] = { ...newOpcoes[index], texto: e.target.value };
                        setNodes((nds) =>
                          nds.map((node) =>
                            node.id === selectedNode.id
                              ? { ...node, data: { ...node.data, opcoes: newOpcoes } }
                              : node
                          )
                        );
                      }}
                      className={`w-full px-2 py-1 text-sm rounded border mb-1 ${
                        darkMode
                          ? 'bg-zinc-700 border-gray-600 text-white'
                          : 'bg-white border-gray-300 text-gray-900'
                      }`}
                    />
                    <select
                      value={opcao.proxima_etapa_id || ''}
                      onChange={(e) => {
                        const newOpcoes = [...(selectedNode.data.opcoes || [])];
                        newOpcoes[index] = { ...newOpcoes[index], proxima_etapa_id: e.target.value || null };
                        setNodes((nds) =>
                          nds.map((node) =>
                            node.id === selectedNode.id
                              ? { ...node, data: { ...node.data, opcoes: newOpcoes } }
                              : node
                          )
                        );
                      }}
                      className={`w-full px-2 py-1 text-sm rounded border ${
                        darkMode
                          ? 'bg-zinc-700 border-gray-600 text-white'
                          : 'bg-white border-gray-300 text-gray-900'
                      }`}
                    >
                      <option value="">Próxima etapa...</option>
                      {nodes.filter(n => n.id !== selectedNode.id).map(n => (
                        <option key={n.id} value={n.id}>{n.data.label}</option>
                      ))}
                    </select>
                  </div>
                ))}
                <button
                  onClick={() => {
                    // A Meta aceita no máximo 10 opções por menu (lista). Bloqueia
                    // aqui em vez de cortar silenciosamente no envio.
                    if ((selectedNode.data.opcoes || []).length >= 10) {
                      toast.error('O WhatsApp permite no máximo 10 opções por menu.');
                      return;
                    }
                    const newOpcoes = [...(selectedNode.data.opcoes || []), { opcao: '', texto: '', proxima_etapa_id: null }];
                    setNodes((nds) =>
                      nds.map((node) =>
                        node.id === selectedNode.id
                          ? { ...node, data: { ...node.data, opcoes: newOpcoes } }
                          : node
                      )
                    );
                  }}
                  className={`w-full px-3 py-2 text-sm rounded border-2 border-dashed ${
                    darkMode
                      ? 'border-gray-600 hover:border-green-500 text-gray-400 hover:text-white'
                      : 'border-gray-300 hover:border-green-500 text-gray-600 hover:text-gray-900'
                  }`}
                >
                  + Adicionar Opção
                </button>
              </div>
            )}

            {/* Mensagem de fallback (opção inválida) — só para menu */}
            {selectedNode.type === 'menu' && (
              <div className="mt-3">
                <label className={`block text-xs font-medium mb-1 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                  Mensagem quando resposta inválida <span className="text-gray-400">(opcional)</span>
                </label>
                <textarea
                  placeholder="Desculpe, não entendi. Por favor, utilize um dos botões acima para responder."
                  value={selectedNode.data.mensagem_fallback || ''}
                  onChange={(e) => {
                    setNodes((nds) => nds.map((node) => node.id === selectedNode.id
                      ? { ...node, data: { ...node.data, mensagem_fallback: e.target.value || null } }
                      : node
                    ));
                  }}
                  rows={2}
                  className={`w-full px-2 py-1.5 text-sm rounded border ${darkMode ? 'bg-zinc-700 border-zinc-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
                />
              </div>
            )}

            {/* Campo de Tag para Atendimento */}
            {selectedNode.type !== 'finalizar' && (
              <div className={`p-3 rounded-lg border-2 ${darkMode ? 'border-zinc-600 bg-zinc-700/50' : 'border-gray-300 bg-gray-50'}`}>
                {/* Tag aplicada ao contato nesta etapa */}
                <div className="mb-3">
                  <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    🏷️ Tag aplicada ao contato
                  </label>
                  <TagSelector
                    value={selectedNode.data.tag_atendimento_id}
                    onChange={(tagId) => {
                      setNodes((nds) =>
                        nds.map((node) =>
                          node.id === selectedNode.id
                            ? { ...node, data: { ...node.data, tag_atendimento_id: tagId } }
                            : node
                        )
                      );
                    }}
                    clienteId={clienteId}
                    darkMode={darkMode}
                    showLabel={false}
                  />
                  <p className={`text-xs mt-1 ${darkMode ? 'text-gray-500' : 'text-gray-500'}`}>
                    Quando o contato passar por esta etapa, essa tag será aplicada
                  </p>
                </div>

                {/* Enviar para atendimento humano */}
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="enviar-atendimento"
                    checked={selectedNode.data.enviar_para_atendimento || false}
                    onChange={(e) => {
                      setNodes((nds) =>
                        nds.map((node) =>
                          node.id === selectedNode.id
                            ? { ...node, data: { ...node.data, enviar_para_atendimento: e.target.checked } }
                            : node
                        )
                      );
                    }}
                    className="w-4 h-4 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
                  />
                  <label htmlFor="enviar-atendimento" className={`text-sm font-medium cursor-pointer ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    Enviar para atendimento humano
                  </label>
                </div>
              </div>
            )}

              </div>
            </div>
            <div className={`p-4 border-t ${darkMode ? 'border-gray-700 bg-zinc-900' : 'border-gray-200 bg-gray-50'}`}>
              <div className="flex gap-2">
                <button
                  onClick={() => {
                    const maxY = Math.max(...nodes.map(n => n.position.y));
                    const newNode = {
                      id: `node-${Date.now()}`,
                      type: selectedNode.type,
                      position: { x: selectedNode.position.x + 50, y: maxY + 250 },
                      data: {
                        ...selectedNode.data,
                        label: `${selectedNode.data.label} (cópia)`,
                        isInicial: false,
                        proxima_etapa_id: null,
                      },
                    };
                    setNodes((nds) => [...nds, newNode]);
                    setSelectedNode(newNode);
                  }}
                  className="flex-1 px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center justify-center space-x-2 text-sm"
                >
                  <Plus className="w-4 h-4" />
                  <span>Duplicar</span>
                </button>
                <button
                  onClick={() => {
                    setConfirmModal({
                      isOpen: true,
                      title: 'Deletar Nó',
                      message: `Tem certeza que deseja deletar "${selectedNode.data.label}"?\n\nEsta ação não pode ser desfeita.`,
                      onConfirm: () => {
                        setNodes((nds) => nds.filter(n => n.id !== selectedNode.id));
                        setEdges((eds) => eds.filter(e => e.source !== selectedNode.id && e.target !== selectedNode.id));
                        setSelectedNode(null);
                        setShowEditModal(false);
                      }
                    });
                  }}
                  className="flex-1 px-3 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 flex items-center justify-center space-x-2 text-sm"
                >
                  <Trash2 className="w-4 h-4" />
                  <span>Deletar</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      </div>
      </div>
    </>
  );
};

const FluxoVisualEditor = (props) => (
  <ReactFlowProvider>
    <FluxoVisualEditorInner {...props} />
  </ReactFlowProvider>
);

export default FluxoVisualEditor;
